// Push subscription helper for Hood — routes to Capacitor native push
// on iOS/Android, and falls back to Web Push (VAPID + service worker) on
// browsers and PWA installs.
import { api } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Detect native platform (iOS/Android via Capacitor). Sync so it can be
// used inside render — the Capacitor import is present in the bundle when
// running under the native shell.
export function isNativeApp() {
  try {
    return typeof window !== "undefined"
      && !!window.Capacitor
      && typeof window.Capacitor.isNativePlatform === "function"
      && window.Capacitor.isNativePlatform();
  } catch { return false; }
}

export function isPushSupported() {
  // Native app always supports push (via APNs/FCM)
  if (isNativeApp()) return true;
  // Web fallback
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPushSubscription() {
  if (isNativeApp()) {
    // On native we track subscription via OS permission, not a PushSubscription
    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const status = await PushNotifications.checkPermissions();
      return status?.receive === "granted" ? { native: true } : null;
    } catch { return null; }
  }
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
  // ===== Native (iOS/Android) =====
  if (isNativeApp()) {
    const { initNativePush } = await import("./native-push");
    const res = await initNativePush();
    if (res.permission === "android_permission_only") {
      // Permission granted at the OS level; FCM delivery activates once
      // Firebase is wired in a future build.
      return { native: true, androidPermissionOnly: true };
    }
    if (res.permission === "android_disabled") {
      throw new Error(
        "الإشعارات الأصلية على الأندرويد تحت الإعداد — استخدم متصفح Chrome للحصول على الإشعارات مؤقتاً"
      );
    }
    if (res.permission !== "granted") {
      throw new Error(
        res.permission === "denied"
          ? "رُفض الإذن — فعّله من إعدادات الجهاز"
          : "تعذّر تفعيل الإشعارات"
      );
    }
    return { native: true };
  }

  // ===== Web =====
  if (!isPushSupported()) {
    throw new Error("هذا المتصفّح لا يدعم الإشعارات");
  }
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js");
    await navigator.serviceWorker.ready;
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("تم رفض إذن الإشعارات. فعّله من إعدادات المتصفّح يدوياً.");
  }
  const { data } = await api.get("/push/public-key");
  if (!data?.public_key) {
    throw new Error("إعدادات Push غير مهيّأة على الخادم");
  }
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await api.post("/push/subscribe", existing.toJSON());
    return existing;
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

export async function unsubscribeFromPush() {
  // On native, we can't programmatically revoke OS permission — the user
  // must toggle it in Settings. Just return false so the UI can guide them.
  if (isNativeApp()) return false;
  const sub = await getPushSubscription();
  if (!sub || sub.native) return false;
  try {
    await api.post("/push/unsubscribe", sub.toJSON());
  } catch (e) { /* ignore */ }
  return sub.unsubscribe();
}

export async function sendTestPush() {
  await api.post("/push/test");
}
